import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, ExternalLink } from 'lucide-react';
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead
} from '../services/api';
import './NotificationBell.css';
import { useNotification } from '../context/NotificationContext';

const NotificationBell = () => {
  const { notificationPermission, enableNotifications } = useNotification();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadCount();
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await getNotifications();
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!showDropdown) {
      fetchNotifications();
    }
    setShowDropdown(!showDropdown);
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if not already read
      if (!notification.isRead) {
        await markNotificationRead(notification._id);
        fetchUnreadCount();
        fetchNotifications();
      }

      // Navigate to action URL if exists
      if (notification.actionUrl) {
        if (notification.actionUrl.startsWith('http')) {
          window.open(notification.actionUrl, '_blank');
        } else {
          navigate(notification.actionUrl);
          setShowDropdown(false);
        }
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      fetchUnreadCount();
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleViewAll = () => {
    navigate('/notifications');
    setShowDropdown(false);
  };

  const handleEnableNotifications = async () => {
    try {
      setLoading(true);
      await enableNotifications();
      // Permission status will update via context
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      announcement: '#2196F3',
      alert: '#f44336',
      update: '#4CAF50',
      info: '#00bcd4',
      warning: '#ff9800',
      success: '#8bc34a'
    };
    return colors[type] || '#2196F3';
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className={`notification-bell-button ${unreadCount > 0 ? 'has-notifications' : ''}`}
        onClick={handleBellClick}
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h3>Notifications</h3>
            <div className="notification-header-actions">
              {unreadCount > 0 && (
                <button
                  className="mark-all-read-btn"
                  onClick={handleMarkAllRead}
                  title="Mark all as read"
                >
                  <Check size={16} />
                  Mark all read
                </button>
              )}
              <button
                className="close-dropdown-btn"
                onClick={() => setShowDropdown(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Permission request banner */}
          {notificationPermission === 'default' && (
            <div className="notification-permission-banner" style={{ padding: '12px', background: '#f0f9ff', borderBottom: '1px solid #e0f2fe' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                <Bell size={16} color="#0284c7" />
                <p style={{ margin: 0, fontSize: '13px', color: '#0c4a6e', lineHeight: 1.4 }}>
                  Enable push notifications to get instant updates on your device
                </p>
              </div>
              <button
                onClick={handleEnableNotifications}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#0284c7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                Enable Notifications
              </button>
            </div>
          )}

          <div className="notification-dropdown-body">
            {loading ? (
              <div className="notification-loading">
                <div className="notification-spinner"></div>
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <Bell size={48} />
                <p>No notifications yet</p>
                <span>We'll notify you when something arrives</span>
              </div>
            ) : (
              <div className="notification-list">
                {notifications.slice(0, 10).map((notification) => (
                  <div
                    key={notification._id}
                    className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-item-icon" style={{ background: `${getTypeColor(notification.type)}20` }}>
                      <span style={{ fontSize: '20px' }}>{notification.icon}</span>
                    </div>
                    <div className="notification-item-content">
                      <div className="notification-item-header">
                        <h4>{notification.title}</h4>
                        {!notification.isRead && <span className="unread-dot"></span>}
                      </div>
                      <p className="notification-item-message">{notification.message}</p>
                      <div className="notification-item-footer">
                        <span className="notification-item-time">{formatTimeAgo(notification.createdAt)}</span>
                        {notification.actionUrl && (
                          <span className="notification-item-action">
                            <ExternalLink size={12} />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-dropdown-footer">
              <button className="view-all-btn" onClick={handleViewAll}>
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
